import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { TableSkeleton } from "@/components/shared/Skeletons";
import {
  CalendarDays,
  CalendarCheck,
  Clock,
  MapPin,
  Pencil,
  Plus,
  Trash2,
  Users,
  Video,
} from "lucide-react";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import StatCard from "@/components/shared/StatCard";
import StatusChip from "@/components/shared/StatusChip";
import EmptyState from "@/components/shared/EmptyState";
import { IconAction } from "@/components/shared/IconAction";
import { Field } from "@/components/forms";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuthStore } from "@/store/authStore";
import { useAdminSeminars, useCreateSeminar, useUpdateSeminar, useDeleteSeminar } from "@/hooks/useAdminData";
import { DatePicker } from "@/components/ui/date-picker";

export const Route = createFileRoute("/admin/seminars/")({
  component: AdminSeminars,
});

interface SessionFieldsProps {
  title: string;
  setTitle: (v: string) => void;
  host: string;
  setHost: (v: string) => void;
  type: string;
  setType: (v: string) => void;
  date: string;
  setDate: (v: string) => void;
  time: string;
  setTime: (v: string) => void;
  location: string;
  setLocation: (v: string) => void;
  withPlaceholders?: boolean;
}

function SessionFields(p: SessionFieldsProps) {
  const ph = (text: string) => (p.withPlaceholders ? text : undefined);
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <Field label="Session title" className="sm:col-span-2">
        <Input
          value={p.title}
          onChange={(e) => p.setTitle(e.target.value)}
          placeholder={ph("e.g. Industrial Training Briefing")}
        />
      </Field>
      <Field label="Lead coordinator">
        <Input
          value={p.host}
          onChange={(e) => p.setHost(e.target.value)}
          placeholder={ph("Facilitator name")}
        />
      </Field>
      <Field label="Engagement type">
        {({ id }) => (
          <Select value={p.type} onValueChange={p.setType}>
            <SelectTrigger id={id}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="on-site">On-site Workshop</SelectItem>
              <SelectItem value="virtual">Virtual Webinar</SelectItem>
            </SelectContent>
          </Select>
        )}
      </Field>
      <Field label="Execution date">
        <DatePicker value={p.date} onChange={(v) => p.setDate(v)} />
      </Field>
      <Field label="Start time">
        <Input type="time" value={p.time} onChange={(e) => p.setTime(e.target.value)} />
      </Field>
      <Field label="Venue / connection link" className="sm:col-span-2">
        <Input
          value={p.location}
          onChange={(e) => p.setLocation(e.target.value)}
          placeholder={ph("Physical location or URL")}
        />
      </Field>
    </div>
  );
}

function AdminSeminars() {
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSeminar, setSelectedSeminar] = useState<any>(null);
  const { user } = useAuthStore();
  const { data: seminarsData = [], isLoading } = useAdminSeminars();
  const createSeminar = useCreateSeminar();
  const updateSeminar = useUpdateSeminar();
  const deleteSeminar = useDeleteSeminar();

  const [formTitle, setFormTitle] = useState("");
  const [formHost, setFormHost] = useState("");
  const [formDate, setFormDate] = useState("");
  const [formTime, setFormTime] = useState("");
  const [formLocation, setFormLocation] = useState("");
  const [formType, setFormType] = useState("on-site");

  const [editTitle, setEditTitle] = useState("");
  const [editHost, setEditHost] = useState("");
  const [editDate, setEditDate] = useState("");
  const [editTime, setEditTime] = useState("");
  const [editLocation, setEditLocation] = useState("");
  const [editType, setEditType] = useState("on-site");

  const displaySeminars = user?.isDemo ? seminarsData : seminarsData;

  const handleCreate = () => {
    createSeminar.mutate(
      {
        title: formTitle,
        host: formHost,
        date: formDate,
        time: formTime,
        location: formLocation,
        type: formType,
      },
      {
        onSettled: () => {
          setScheduleOpen(false);
          setFormTitle("");
          setFormHost("");
          setFormDate("");
          setFormTime("");
          setFormLocation("");
          setFormType("on-site");
        },
      }
    );
  };

  const openEditDialog = (sem: any) => {
    setSelectedSeminar(sem);
    setEditTitle(sem.title || "");
    setEditHost(sem.host || "");
    setEditDate(sem.date || "");
    setEditTime(sem.time || "");
    setEditLocation(sem.location || "");
    setEditType(sem.type || "on-site");
    setEditDialogOpen(true);
  };

  const handleEdit = () => {
    updateSeminar.mutate(
      {
        id: selectedSeminar.id,
        payload: {
          title: editTitle,
          host: editHost,
          date: editDate,
          time: editTime,
          location: editLocation,
          type: editType,
        },
      },
      { onSettled: () => setEditDialogOpen(false) }
    );
  };

  const handleDelete = () => {
    deleteSeminar.mutate(selectedSeminar.id, { onSettled: () => setDeleteDialogOpen(false) });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Training & seminars"
        subtitle="Coordinate professional development workshops, industrial training orientations, and guest lectures."
        breadcrumbs={[{ label: "Dashboard", path: "/admin/dashboard" }, { label: "Seminars" }]}
        action={
          <Button onClick={() => setScheduleOpen(true)}>
            <Plus />
            Schedule session
          </Button>
        }
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 md:gap-4">
        <StatCard
          label="Upcoming sessions"
          value={displaySeminars.length || "0"}
          icon={<CalendarDays />}
        />
        <StatCard
          label="Total participants"
          value={displaySeminars.reduce((acc: number, s: any) => acc + (s.attendees || 0), 0) || "0"}
          icon={<Users />}
        />
        <StatCard
          label="Completion rate"
          value={displaySeminars.length > 0 ? "100%" : "-"}
          icon={<CalendarCheck />}
        />
      </div>

      <SectionCard title="Active training schedule" noPadding>
        {isLoading ? (
          <div className="p-4">
            <TableSkeleton rows={5} columns={4} />
          </div>
        ) : displaySeminars.length === 0 ? (
          <EmptyState
            bare
            icon={<CalendarDays />}
            title="No upcoming seminars scheduled."
            description="Schedule a session to start building the training calendar."
          />
        ) : (
          <ul className="divide-y divide-border">
            {displaySeminars.map((seminar: any) => (
              <li
                key={seminar.id}
                className="grid gap-3 px-4 py-4 transition-colors hover:bg-muted/40 md:grid-cols-12 md:items-center md:gap-4 md:px-5"
              >
                <div className="flex min-w-0 items-center gap-3 md:col-span-5">
                  <span
                    className="flex size-9 shrink-0 items-center justify-center rounded-sm border border-border bg-background text-body [&_svg]:size-4"
                    aria-hidden
                  >
                    {seminar.type === "virtual" ? <Video /> : <MapPin />}
                  </span>
                  <div className="min-w-0">
                    <h3 className="truncate text-sm font-medium text-foreground">{seminar.title}</h3>
                    <p className="truncate text-caption text-muted-foreground">Led by {seminar.host}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-caption md:col-span-4">
                  <span className="inline-flex items-center gap-1.5 text-body tabular-nums">
                    <CalendarDays className="size-3.5 text-muted-foreground" aria-hidden />
                    {seminar.date}
                  </span>
                  <span className="inline-flex min-w-0 items-center gap-1.5 text-body">
                    <MapPin className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
                    <span className="truncate">{seminar.location}</span>
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-muted-foreground tabular-nums">
                    <Clock className="size-3.5" aria-hidden />
                    {seminar.time}
                  </span>
                  <span className="text-muted-foreground tabular-nums">
                    {seminar.attendees} attendees expected
                  </span>
                </div>
                <div className="flex items-center justify-between gap-1 md:col-span-3 md:justify-end">
                  {seminar.status ? <StatusChip status={seminar.status} /> : <span />}
                  <div className="flex gap-0.5 md:ml-2">
                    <IconAction
                      label="Edit seminar"
                      icon={<Pencil />}
                      onClick={() => openEditDialog(seminar)}
                    />
                    <IconAction
                      label="Delete seminar"
                      tone="destructive"
                      icon={<Trash2 />}
                      onClick={() => {
                        setSelectedSeminar(seminar);
                        setDeleteDialogOpen(true);
                      }}
                    />
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>

      <Dialog open={scheduleOpen} onOpenChange={(o) => !o && setScheduleOpen(false)}>
        <DialogContent className="max-h-[calc(100dvh-2rem)] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Schedule session</DialogTitle>
            <DialogDescription>Add a workshop, orientation or guest lecture to the calendar.</DialogDescription>
          </DialogHeader>
          <SessionFields
            withPlaceholders
            title={formTitle}
            setTitle={setFormTitle}
            host={formHost}
            setHost={setFormHost}
            type={formType}
            setType={setFormType}
            date={formDate}
            setDate={setFormDate}
            time={formTime}
            setTime={setFormTime}
            location={formLocation}
            setLocation={setFormLocation}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate}>Create session</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editDialogOpen} onOpenChange={(o) => !o && setEditDialogOpen(false)}>
        <DialogContent className="max-h-[calc(100dvh-2rem)] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit session</DialogTitle>
            <DialogDescription>Update the details participants see for this session.</DialogDescription>
          </DialogHeader>
          <SessionFields
            title={editTitle}
            setTitle={setEditTitle}
            host={editHost}
            setHost={setEditHost}
            type={editType}
            setType={setEditType}
            date={editDate}
            setDate={setEditDate}
            time={editTime}
            setTime={setEditTime}
            location={editLocation}
            setLocation={setEditLocation}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEdit}>Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={(o) => !o && setDeleteDialogOpen(false)}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete session</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete{" "}
              <strong className="font-medium text-foreground">{selectedSeminar?.title}</strong>? This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button variant="destructive" onClick={handleDelete}>
              <Trash2 />
              Delete permanently
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
